import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Pagination,
  TextField,
} from "@mui/material";
import { useGetTransactionHistoryQuery, useSearchTransactionHistoryQuery } from "../store/services/transactionApi";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { cellSX } from "../component/common/commonStyle";
import moment from "moment";

const TransactionHistory = () => {
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState(dayjs());
  const [toDate, setToDate] = useState(dayjs());
  const [page, setPage] = useState(1);
  const limit = 20;

  const {
    data,
    isLoading,
    isError,
    error,
  } = useSearchTransactionHistoryQuery({
    fromDate: fromDate.format("YYYY-MM-DD"),
    toDate: toDate.format("YYYY-MM-DD"),
    page,
    limit,
    search,
  });


  const transactions = useMemo(() => data?.transactions || [], [data]);
  const totalPages = useMemo(
    () => Math.ceil((data?.total || 0) / limit),
    [data],
  );

  const handlePageChange = (event, value) => {
    setPage(value);
  };
  const inputSx = {
    borderRadius: "0.5rem",
    minWidth: 140,
    "& .MuiOutlinedInput-root": {
      background: "rgba(255,255,255,0.7)",
      "& fieldset": { borderColor: "#E2E8F0" },
      "&:hover fieldset": { borderColor: "#94A3B8" },
    },
    "& .MuiInputLabel-root": { color: "#475569" },
    "& .Mui-disabled": {
      "& .MuiOutlinedInput-input": {
        color: "#334155 !important",
        WebkitTextFillColor: "#334155 !important",
      },
      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#CBD5E1" },
    },
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Paper elevation={3} sx={{ padding: 2 }}>
        <Typography
          variant="h6"
          sx={{ mb: 2, fontSize: "0.9rem", fontWeight: "bold" }}
        >
          Transaction History
        </Typography>

        {/* DATE FILTER */}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", elevation: 2, borderRadius: "0.5rem", padding: "1rem", backgroundColor: "#ffffff52", backdropFilter: "blur(10px)", boxShadow: "0 4px 12px rgba(14,165,233,0.1)", mb: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            {/* FROM DATE */}
            <DatePicker
              label="From"
              value={fromDate}
              onChange={(date) => {
                setFromDate(date);
                setPage(1);

                // 🔥 1 month validation (same as ScheduledSMS)
                if (toDate && date && toDate.diff(date, "month", true) > 1) {
                  setToDate(date.add(1, "month"));
                }
              }}
              maxDate={dayjs()}
              format="DD/MM/YYYY"
              sx={{ ...inputSx, width: 140 }}
              slotProps={{ textField: { size: "small" } }}
            />

            {/* TO DATE */}
            <DatePicker
              label="To"
              value={toDate}
              onChange={(date) => {
                setToDate(date);
                setPage(1);
              }}
              minDate={fromDate}
              maxDate={
                fromDate
                  ? fromDate.add(1, "month").isBefore(dayjs())
                    ? fromDate.add(1, "month")
                    : dayjs()
                  : dayjs()
              }
              format="DD/MM/YYYY"
              sx={{ ...inputSx, width: 140 }}
              slotProps={{ textField: { size: "small" } }}
            />
            <TextField
              label="Search Customer"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1); // reset page
              }}
              size="small"
              sx={inputSx}
              slotProps={{ textField: { size: "small" } }}
            />
          </LocalizationProvider>
        </Box>

        {/* TABLE */}
        <TableContainer
          component={Paper}
          sx={{ borderRadius: 0.5, border: "1px solid #1b1b1bff" }}
        >
          <Table>
            <TableHead
              sx={{
                bgcolor: "#0288d1",
                "& th": {
                  // fontWeight: "bold",
                  color: "white",
                  // textTransform: "uppercase",
                  borderRight: "0.5px solid #1b1b1bff",
                  borderBottom: "0.5px solid #1b1b1bff",
                  padding: "4px 8px",
                  fontSize: "0.8rem",
                  textAlign: "center",
                },
              }}
            >
              <TableRow >
                <TableCell sx={{ width: "100px" }}>Date</TableCell>
                <TableCell sx={{ width: "100px" }}>From</TableCell>
                <TableCell sx={{ width: "100px" }}>To</TableCell>
                <TableCell sx={{ width: "100px" }}>Debit</TableCell>
                <TableCell sx={{ width: "100px" }}>Credit</TableCell>
                <TableCell>Remark</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                    sx={{ padding: "4px 8px" }}
                  >
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : isError ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                    sx={{ padding: "4px 8px" }}
                  >
                    Error: {error?.message || "Failed to fetch data"}
                  </TableCell>
                </TableRow>
              ) : transactions.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                    sx={{ padding: "4px 8px" }}
                  >
                    No Data Found!
                  </TableCell>
                </TableRow>
              ) : (
                transactions.map((row) => (
                  <TableRow
                    key={row.transaction_id}
                    sx={{
                      "& td": {
                        border: "0.5px solid #1b1b1bff",
                        padding: "4px 8px",
                        height: "44px",
                        textAlign: "center",
                        fontSize: "0.8rem",
                      },
                    }}
                  >
                    <TableCell sx={cellSX}>
                      {" "}
                      {moment(row.date).format("DD/MM/YYYY hh:mm A")}
                    </TableCell>
                    <TableCell sx={cellSX}>{row.fromName}</TableCell>
                    <TableCell sx={cellSX}>{row.toName}</TableCell>
                    <TableCell
                      sx={{
                        color: row.debit > 0 ? "red" : "inherit",
                        fontSize: "0.8rem",
                      }}
                    >
                      {row.debit > 0 ? `₹${row.debit}` : ""}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: row.credit > 0 ? "green" : "inherit",
                        fontSize: "0.8rem",
                      }}
                    >
                      {row.credit > 0 ? `₹${row.credit}` : ""}
                    </TableCell>
                    <TableCell sx={cellSX}>{row.summary}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* PAGINATION */}
        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default TransactionHistory;
